import { Button } from "armstrong-react";
import React from "react";
import { actions, Fieldset, Form } from "react-redux-form";
import _ from "underscore";
import * as DateHelpers from "@travellocal/utils/dateHelpers";
import {
  Avatar,
  FlowBox,
  Logo,
  StarInput,
  NumericalSelector,
} from "../../../../core/atoms";
import { TextareaInput, TextInput } from "../../../../core/atoms/rrf";
import { DateTimePicker } from "../../dateTimePicker";
import { EntityConverter } from "./../../../helpers/entityConverter";
import "./reviewForm.scss";

interface Props {
  review?: IReview;
  enquiry?: IEnquiry;
  lto: ILtoSummary;
  rrfState: Forms.SharedFormRoot;
  dispatch: any;
  userType: UserType;
  submissionText?: string;
  submitBtnText?: string;
  submit?: (data: IReviewInput) => void;
  disabled?: boolean;
}

interface State {
  submitPending: boolean;
  errorMessage?: string;
}

export class ReviewForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      submitPending: false,
      errorMessage: null,
    };
  }

  private rootModelPath = (): string => {
    const review: IReview = this.props.review;
    return review ? `forms.reviews[${review.id}]` : "forms.newReview";
  }

  private updateRRFState = (model: string, value: any): void => {
    const modelPath: string = `${this.rootModelPath()}.${model}`;
    this.props.dispatch(actions.change(modelPath, value));
  }

  private getCustomerNameFromEnquiry = (enquiry: IEnquiry): string => {
    if (enquiry) {
      return `${enquiry.customer.firstName} ${enquiry.customer.lastName[0]}`;
    }

    return "";
  }

  private getCurrentDateTime = (): IDateTime => {
    return DateHelpers.now();
  }

  public componentDidMount() {
    const formState = this.getFormState();

    if (this.props.review && _.isEmpty(formState)) {
      const reviewFormData: Forms.ReviewForm = EntityConverter.reviewToReviewForm(this.props.review);
      this.props.dispatch(actions.merge(this.rootModelPath(), reviewFormData));
    }

    if (!this.props.review && formState && formState.customerInfo && !formState.customerInfo.createdTimestamp) {
      this.updateRRFState("customerInfo.createdTimestamp", this.getCurrentDateTime());
    }
  }

  public handleFormSubmit = (formData: Forms.ReviewForm): void => {
    this.setState({ errorMessage: null, submitPending: true }, async () => {
      const newState: State = { submitPending: false };

      try {
        let reviewInput: IReviewInput = EntityConverter.reviewFormToReviewInput(formData);

        if (this.props.userType === UserType.Administrator) {
          reviewInput = {...reviewInput, ltoId: this.props.lto.id};
        }

        if (this.props.userType === UserType.Customer) {
          reviewInput = {...reviewInput, enquiryId: this.props.enquiry.id};
        }

        await this.props.submit(reviewInput);
        this.props.dispatch(actions.reset(this.rootModelPath()));
      }
      catch (e) {
        const err = e as IFetchApiFailure;
        newState.errorMessage = err.validations ? err.validations.map(x => x.message).join(", ") : err.message;
      }

      this.setState(newState);
    });
  }

  private getFormState = (): Forms.ReviewForm => {
    if (!this.props.rrfState) {
      return {} as Forms.ReviewForm;
    }

    const review = this.props.review;
    if (review) {
      return this.props.rrfState.reviews[review.id] || {} as Forms.ReviewForm;
    } else {
      return this.props.rrfState.newReview;
    }
  }

  public render() {
    const { lto, userType, review, disabled, submit, submissionText, submitBtnText } = this.props;

    const formState: Forms.ReviewForm = this.getFormState();
    const customerInfo: Forms.ReviewCustomerInfo = formState.customerInfo || {} as Forms.ReviewCustomerInfo;
    const overallFeedback: Forms.OverallFeedback = formState.overallFeedback || {} as Forms.OverallFeedback;
    const travelLocalFeedback: Forms.TravelLocalFeedback = formState.travelLocalFeedback || {} as Forms.TravelLocalFeedback;
    const ltoFeedback: Forms.LtoFeedback = formState.ltoFeedback || {} as Forms.LtoFeedback;

    return (
      <Form model={this.rootModelPath()} onSubmit={this.handleFormSubmit} className="new-review-form">
        {this.state.errorMessage && <p className="validation-bar"><h3>Something went wrong! Please try again.</h3>{this.state.errorMessage}</p>}

        {(userType === UserType.Administrator && !review) &&
          <section className="field-group customer-info">
            <h2>Customer details</h2>
            <p>When publishing a review on behalf of a customer, you need to fill in their name and a published date. This will be shown publicly.</p>

            <Fieldset model=".customerInfo">
              <FlowBox>
                <div className="field">
                  <label>Customer name</label>
                  <TextInput model=".customerName" placeholder="e.g. Brian Weatherfax" disabled={disabled} />
                </div>
                <div className="field">
                  <label>Review submission time</label>
                  <DateTimePicker
                    value={customerInfo.createdTimestamp}
                    onChange={v => this.updateRRFState("customerInfo.createdTimestamp", v)}
                    inputDisabled={disabled}
                  />
                </div>
              </FlowBox>
            </Fieldset>
          </section>
        }

        <section className="field-group customer-review">
          <h2>Trip review (1/3)</h2>
          <p>How was your holiday with TravelLocal? Please fill in all fields in this section.</p>

          <Fieldset model=".overallFeedback">
            <div className="field">
              <label>Review title</label>
              <TextInput model=".reviewTitle" placeholder="eg 'Great trip, wonderful guides'" disabled={disabled} />
            </div>

            <FlowBox>
              <div className="field">
                <label>Where are you from?</label>
                <TextInput model=".customerLocation" disabled={disabled} />
              </div>

              <div className="field">
                <label>How would you rate your trip overall?</label>
                <StarInput
                  className="m-top-medium"
                  value={overallFeedback.overallRating}
                  onChange={v => this.updateRRFState("overallFeedback.overallRating", v)}
                  readonly={disabled}
                />
              </div>
            </FlowBox>

            <div className="field long-text">
              <label>Your review</label>
              <TextareaInput model=".overallComment" placeholder="Details are good here!" disabled={disabled} />
            </div>
          </Fieldset>
        </section>

        <section className="field-group travellocal-feedback">
          <Logo width={100} />
          <h2>Feedback for TravelLocal (2/3)</h2>
          <p>Let us know what you thought about the experience of booking through TravelLocal! Please fill in all fields in this section.</p>

          <Fieldset model=".travelLocalFeedback">
            <div className="field">
              <label>How easy did you find it to plan and book your trip via TravelLocal?</label>
              <StarInput
                className="p-top-xxsmall"
                value={travelLocalFeedback.easeOfUseRating}
                onChange={v => this.updateRRFState("travelLocalFeedback.easeOfUseRating", v)}
                readonly={disabled}
              />
            </div>

            <div className="field long-text">
              <label>What could TravelLocal have done better?</label>
              <TextareaInput model=".improvementComment" disabled={disabled} />
            </div>

            <div className="field">
              <label>Where are you planning to travel next? We use this to gauge interest in new destinations for our platform.</label>
              <TextareaInput model=".whereNext" disabled={disabled} />
            </div>

            <div className="field">
              <label>How likely are you to recommend TravelLocal?</label>
              <NumericalSelector
                min={0}
                max={5}
                onChange={v => this.updateRRFState("travelLocalFeedback.howLikelyToRecommend", v)}
                value={(travelLocalFeedback.howLikelyToRecommend != null) ? travelLocalFeedback.howLikelyToRecommend : null}
                disabled={disabled}
              />
            </div>
          </Fieldset>
        </section>

        <section className="field-group lto-feedback">
          <Avatar source={lto.image ? lto.image.thumbnailUrl : null} rounded={true} />

          <h2>Feedback for {lto.name} (3/3)</h2>
          <p>Tell our local partner how they did! Your feedback will let {lto.name} improve the experience for future travellers. This section is optional.</p>

          <Fieldset model=".ltoFeedback">
            <FlowBox>
              <div className="field">
                <label>How would you rate the ease/quality of interaction with {lto.name} during the planning process?</label>
                <StarInput
                  value={ltoFeedback.ltoInteractionRating}
                  onChange={v => this.updateRRFState("ltoFeedback.ltoInteractionRating", v)}
                  readonly={disabled}
                />
              </div>

              <div className="field">
                <label>How would you rate the value for money of your trip?</label>
                <StarInput
                  value={ltoFeedback.valueRating}
                  onChange={v => this.updateRRFState("ltoFeedback.valueRating", v)}
                  readonly={disabled}
                />
              </div>
            </FlowBox>

            <FlowBox>
              <div className="field">
                <label>How would you rate the quality of your vehicles/transportation?</label>
                <StarInput
                  value={ltoFeedback.transportationRating}
                  onChange={v => this.updateRRFState("ltoFeedback.transportationRating", v)}
                  readonly={disabled}
                />
              </div>

              <div className="field">
                <label>Please rate any guides/drivers you had on the trip</label>
                <StarInput
                  value={ltoFeedback.guideRating}
                  onChange={v => this.updateRRFState("ltoFeedback.guideRating", v)}
                  readonly={disabled}
                />
              </div>
            </FlowBox>

            <div className="field long-text">
              <label>What was good about {lto.name}?</label>
              <TextareaInput model={".ltoPositives"} placeholder="What did they do well?" disabled={disabled} />
            </div>

            <div className="field long-text">
              <label>What could {lto.name} improve on?</label>
              <TextareaInput model=".ltoImprovements" placeholder="What could they be better at?" disabled={disabled} />
            </div>

            <div className="field long-text">
              <label>Any comments about your guides or drivers?</label>
              <TextareaInput model=".guideComment" disabled={disabled} />
            </div>

            <div className="field long-text">
              <label>What advice would you give for people planning a trip to your destination?</label>
              <TextareaInput model=".locationAdvice" placeholder="What to see/not see? Any places to stay you'd particularly recommend? Any outstanding meals you had?" disabled={disabled} />
            </div>

            <div className="field long-text">
              <label>What was the most memorable part of your trip?</label>
              <TextareaInput model=".favoritePart" placeholder="We'd love to know!" disabled={disabled} />
            </div>
          </Fieldset>
        </section>

        {submit &&
          <section className="field-group submit-row">
            <FlowBox>
              <span>{submissionText}</span>
              <Button className="primary-button heading--4 text--light" type="submit" pending={this.state.submitPending}>{submitBtnText}</Button>
            </FlowBox>
          </section>
        }
      </Form>
    );
  }
}
